import { FilterState, isArrayField } from '@/components/filters';
import { Button } from '@/components/ui/Button';
import { FilterDropdown } from '@/components/ui/FilterDropdown';
import { ListFilter } from '@/components/ui/ListFilter';
import { RangeFilter } from '@/components/ui/RangeFilter';

import {
    BeerStyles,
    Categories,
    PackagingTypes,
    Vendors,
    Volumes,
} from '@/types';
import { RotateCcw } from 'lucide-react';
import React from 'react';

interface TopFiltersProps {
    categoriesById: Categories;
    beerStylesById: BeerStyles;
    vendorsById: Vendors;
    packagingTypesById: PackagingTypes;
    volumesById: Volumes;
    filters: FilterState;
    setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
    onApply: () => void;
}

const handleSort = (
    data: Categories | Vendors | BeerStyles | PackagingTypes | Volumes,
    key: 'name' | 'title' | 'label',
) => {
    return new Map(
        Object.entries(data).sort(([, a], [, b]) => {
            const aVal = a[key];
            const bVal = b[key];

            // Если оба значения строки - используем localeCompare
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return aVal.localeCompare(bVal, 'ru');
            }

            // Если числа - сравниваем напрямую
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return aVal - bVal;
            }

            // Fallback: приводим к строке
            return String(aVal).localeCompare(String(bVal), 'ru');
        }),
    );
};

export function TopFilters({
    categoriesById,
    beerStylesById,
    vendorsById,
    packagingTypesById,
    volumesById,
    filters,
    setFilters,
    onApply,
}: TopFiltersProps) {
    const toggleFilter = (key: keyof FilterState, value: string) => {
        setFilters((prev: FilterState) => {
            const current = prev[key] as string[] | undefined;
            const exists = current && current.includes(value);
            return {
                ...prev,
                [key]: exists
                    ? current.filter((item) => item !== value)
                    : current
                      ? [...current, value]
                      : [value],
            };
        });
    };

    const clearFilter = (key: keyof FilterState) => {
        setFilters((prev: FilterState) => ({
            ...prev,
            [key]: [],
        }));
    };

    const resetFilters = () => {
        setFilters({});
        // onApply(initialValues);
    };

    const hasActiveFilters = Object.keys(filters).length > 0;
    const isActiveFilter = (filterKey: keyof FilterState) => {
        return filterKey in filters;
    };
    const filterCount = (filterKey: keyof FilterState) => {
        return isArrayField(filterKey)
            ? isActiveFilter(filterKey)
                ? filters !== undefined
                    ? filters[filterKey]?.length
                    : undefined
                : undefined
            : undefined;
    };

    // const categories = Object.keys(categoriesById);
    // const beerStyles = Object.keys(beerStylesById);
    // const vendors = Object.keys(vendorsById);
    // const volumes = Object.keys(volumesById);
    // const packagingTypes = Object.keys(packagingTypesById);

    const categories = Array.from(handleSort(categoriesById, 'name').keys());
    const beerStyles = Array.from(handleSort(beerStylesById, 'name').keys());
    const vendors = Array.from(handleSort(vendorsById, 'name').keys());
    const volumes = Array.from(handleSort(volumesById, 'title').keys());
    const packagingTypes = Array.from(
        handleSort(packagingTypesById, 'label').keys(),
    );

    return (
        <div className="mb-8 hidden flex-col gap-5 border-b border-zinc-800/50 pb-6 md:flex">
            <div className="flex flex-wrap items-center gap-2.5">
                <FilterDropdown
                    label="Категория"
                    isActive={isActiveFilter('category')}
                    badge={filterCount('category')}
                >
                    <ListFilter
                        label="Категория"
                        items={categories}
                        renderLabel={(item) => categoriesById[item].name}
                        selectedItems={filters.category ?? []}
                        onChange={(val) => toggleFilter('category', val)}
                        getCounts={(val) => categoriesById[val].pCount}
                        hideLabel
                        onClear={() => clearFilter('category')}
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Производитель"
                    isActive={isActiveFilter('vendor')}
                    badge={filterCount('vendor')}
                >
                    <ListFilter
                        label="Производитель"
                        items={vendors}
                        renderLabel={(item) => vendorsById[item].name}
                        selectedItems={filters.vendor ?? []}
                        onChange={(val) => toggleFilter('vendor', val)}
                        getCounts={(val) => vendorsById[val].pCount}
                        hideLabel
                        scrollable={beerStyles.length > 15}
                        onClear={() => clearFilter('vendor')}
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Стиль"
                    isActive={isActiveFilter('style')}
                    badge={filterCount('style')}
                >
                    <ListFilter
                        label="Стиль"
                        items={beerStyles}
                        renderLabel={(item) => beerStylesById[item].name}
                        selectedItems={filters.style ?? []}
                        onChange={(val) => toggleFilter('style', val)}
                        getCounts={(val) => beerStylesById[val].pCount}
                        hideLabel
                        scrollable={beerStyles.length > 15}
                        onClear={() => clearFilter('style')}
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Цена, ₽"
                    isActive={
                        filters?.minPrice !== undefined ||
                        filters?.maxPrice !== undefined
                    }
                >
                    <RangeFilter
                        label="Цена, ₽"
                        minValue={filters.minPrice}
                        maxValue={filters.maxPrice}
                        onChange={(type, value) =>
                            setFilters((prev) => ({
                                ...prev,
                                [type === 'min' ? 'minPrice' : 'maxPrice']:
                                    value,
                            }))
                        }
                        step={10}
                        hideLabel
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Тара"
                    isActive={isActiveFilter('packagingType')}
                    badge={filterCount('packagingType')}
                >
                    <ListFilter
                        label="Тара"
                        items={packagingTypes}
                        renderLabel={(item) =>
                            String(packagingTypesById[item].label)
                        }
                        selectedItems={filters.packagingType ?? []}
                        onChange={(val) => toggleFilter('packagingType', val)}
                        hideLabel
                        scrollable={packagingTypes.length > 15}
                        onClear={() => clearFilter('packagingType')}
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Объем, л"
                    isActive={isActiveFilter('volume')}
                    badge={filterCount('volume')}
                >
                    <ListFilter
                        label="Объем, л"
                        items={volumes}
                        renderLabel={(item) => String(volumesById[item].title)}
                        selectedItems={filters.volume ?? []}
                        onChange={(val) => toggleFilter('volume', val)}
                        hideLabel
                        scrollable={volumes.length > 15}
                        onClear={() => clearFilter('volume')}
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Крепость (ABV, %)"
                    isActive={
                        filters?.minAbv !== undefined ||
                        filters?.maxAbv !== undefined
                    }
                >
                    <RangeFilter
                        label="Крепость (ABV)"
                        minValue={filters.minAbv}
                        maxValue={filters.maxAbv}
                        onChange={(type, value) =>
                            setFilters((prev) => ({
                                ...prev,
                                [type === 'min' ? 'minAbv' : 'maxAbv']: value,
                            }))
                        }
                        step={0.1}
                        hideLabel
                    />
                </FilterDropdown>

                <FilterDropdown
                    label="Горечь (IBU)"
                    isActive={
                        filters?.minIbu !== undefined ||
                        filters?.maxIbu !== undefined
                    }
                >
                    <RangeFilter
                        label="Горечь (IBU)"
                        minValue={filters.minIbu}
                        maxValue={filters.maxIbu}
                        onChange={(type, value) =>
                            setFilters((prev) => ({
                                ...prev,
                                [type === 'min' ? 'minIbu' : 'maxIbu']: value,
                            }))
                        }
                        step={1}
                        hideLabel
                    />
                </FilterDropdown>
                {hasActiveFilters && (
                    <button
                        onClick={resetFilters}
                        className="group ml-2 flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold tracking-widest text-zinc-500 uppercase transition-colors hover:text-red-400"
                    >
                        <RotateCcw className="h-3.5 w-3.5 transition-transform group-hover:-rotate-45" />
                        Сбросить
                    </button>
                )}
            </div>

            <div className="ml-auto flex items-center gap-2">
                <Button
                    variant="primary"
                    onClick={() => onApply()}
                    className="h-10 rounded-md px-6 text-[11px] font-bold tracking-[0.2em] uppercase shadow-lg shadow-brand-gold/10"
                >
                    Применить
                </Button>
            </div>
        </div>
    );
}
